import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { authAdapter } from '../api/authAdapter';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { useLocale } from '../context/LocaleContext';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { locale, toggleLocale, t } = useLocale();

  // 同時檢查 HashRouter 與原生 window.location.search
  const token = searchParams.get('token') || new URLSearchParams(window.location.search).get('token');

  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false); // 🌟 新增成功狀態

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await authAdapter.resetPassword(newPassword, token);
      // 🌟 移除原生 alert，改為觸發成功狀態 UI
      setIsSuccess(true);
    } catch (err) {
      console.error("Reset Error:", err);
      const msg = err.message || '';
      if (msg.includes('least 8 characters')) {
        setError(t('errors.passwordLength'));
      } else {
        setError(msg || t('errors.default'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex flex-col p-4 relative overflow-hidden items-center justify-center">
        <button
          onClick={toggleLocale}
          className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/80 dark:bg-slate-800/80 hover:bg-white dark:hover:bg-slate-700 backdrop-blur-md text-gray-600 dark:text-gray-300 transition-all font-bold text-sm shadow-sm z-50 flex items-center justify-center border border-gray-100 dark:border-slate-700"
        >
          {locale === 'zh-TW' ? 'EN' : '中'}
        </button>
        <div className="w-full max-w-md bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-[2.5rem] shadow-2xl p-8 border-4 border-white dark:border-slate-800 relative text-center">
          <SafeIcon name="AlertTriangle" className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">{t('login.invalidToken')}</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8 font-medium">{t('login.invalidTokenDesc')}</p>
          <button
            onClick={() => navigate('/login')}
            className="w-full py-4 bg-gray-900 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-2xl font-bold shadow-lg active:scale-95 transition-all flex justify-center items-center gap-2"
          >
            <SafeIcon name="ArrowLeft" className="w-4 h-4" />
            {t('login.backBtn')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col p-4 relative overflow-hidden">
      <button
        onClick={toggleLocale}
        className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/80 dark:bg-slate-800/80 hover:bg-white dark:hover:bg-slate-700 backdrop-blur-md text-gray-600 dark:text-gray-300 transition-all font-bold text-sm shadow-sm z-50 flex items-center justify-center border border-gray-100 dark:border-slate-700"
      >
        {locale === 'zh-TW' ? 'EN' : '中'}
      </button>

      {/* 裝飾背景 */}
      <div className="fixed top-10 left-10 w-32 h-32 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
      <div className="fixed top-10 right-10 w-32 h-32 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
      <div className="fixed bottom-10 left-20 w-32 h-32 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>

      <div className="flex-1 flex items-center justify-center z-10 w-full mt-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-[2.5rem] shadow-2xl p-8 border-4 border-white dark:border-slate-800 relative overflow-hidden"
        >
          <AnimatePresence mode="wait">
            {isSuccess ? (
              // 🌟 成功狀態 UI
              <motion.div
                key="success"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="text-center py-4"
              >
                <div className="mx-auto w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-500 rounded-[2rem] flex items-center justify-center shadow-lg shadow-green-200 dark:shadow-none rotate-3 mb-8">
                  <SafeIcon name="Check" className="w-12 h-12 text-white" />
                </div>
                <h2 className="text-3xl font-black text-gray-800 dark:text-white tracking-tight mb-4">
                  {locale === 'zh-TW' ? '重設成功！' : 'Success!'}
                </h2>
                <p className="text-gray-500 dark:text-gray-400 mb-10 font-medium text-lg leading-relaxed">
                  {t('login.updateSuccess')}
                </p>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full py-4 bg-gray-900 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-2xl font-bold shadow-lg active:scale-95 transition-all flex justify-center items-center gap-2"
                >
                  {t('login.loginBtn')}
                </button>
              </motion.div>
            ) : (
              // 密碼重設表單 UI
              <motion.div
                key="form"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <div className="text-center mb-8">
                  <div className="mx-auto w-20 h-20 bg-gradient-to-br from-emerald-400 to-teal-400 rounded-[1.5rem] flex items-center justify-center shadow-lg shadow-emerald-200 dark:shadow-none rotate-3 mb-6">
                    <SafeIcon name="Unlock" className="w-10 h-10 text-white" />
                  </div>
                  <h2 className="text-3xl font-black text-gray-800 dark:text-white tracking-tight">{t('login.resetTitle')}</h2>
                </div>

                <form className="space-y-5" onSubmit={handleSubmit}>
                  <div>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        required
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full pl-5 pr-12 py-4 bg-gray-50 dark:bg-slate-800 border-2 border-gray-100 dark:border-slate-700 rounded-2xl focus:ring-4 focus:ring-emerald-100 dark:focus:ring-emerald-900/30 focus:border-emerald-300 dark:focus:border-emerald-500 outline-none transition-all font-medium text-gray-800 dark:text-gray-200"
                        placeholder={t('login.newPassword')}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-1"
                      >
                        <SafeIcon name={showPassword ? "EyeOff" : "Eye"} className="w-5 h-5" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 ml-2 font-bold">
                      {t('login.passwordHint')}
                    </p>
                  </div>

                  {error && (
                    <div className="text-red-500 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-xl font-bold text-center border border-red-100 dark:border-red-900/50">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-4 bg-gray-900 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-2xl font-bold shadow-lg active:scale-95 transition-all flex justify-center items-center gap-2 mt-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isLoading ? <SafeIcon name="Loader" className="animate-spin w-5 h-5" /> : null}
                    {isLoading ? t('common.processing') : t('login.updatePasswordBtn')}
                  </button>
                </form>

                <div className="mt-8 flex justify-center">
                  <button
                    onClick={() => navigate('/login')}
                    className="text-sm font-bold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white transition-colors flex items-center gap-1"
                  >
                    <SafeIcon name="ArrowLeft" className="w-4 h-4" />
                    {t('login.backToLogin')}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}